import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[#103254] text-white hover:bg-[#0b2138] " +
          "dark:bg-gradient-to-br dark:from-[#31637f] dark:to-[#22475f] " +
          "dark:text-white dark:hover:from-[#376f8f] dark:hover:to-[#274f6c] " +
          "dark:shadow-[0_4px_10px_-2px_rgba(49,99,127,0.3),inset_0_1px_0_rgba(255,255,255,0.12)] " +
          "dark:border-0",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onPointerDown, onPointerUp, onPointerLeave, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const [pressed, setPressed] = React.useState(false)

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        style={{
          transform: pressed ? 'scale(0.95)' : 'scale(1)',
          opacity: pressed ? 0.8 : 1,
          ...style,
        }}
        onPointerDown={(e) => {
          setPressed(true)
          onPointerDown?.(e)
        }}
        onPointerUp={(e) => {
          setPressed(false)
          onPointerUp?.(e)
        }}
        onPointerLeave={(e) => {
          setPressed(false)
          onPointerLeave?.(e)
        }}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }